/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { DrawingPinFilledIcon, IdCardIcon, PersonIcon, Text } from '@/shared/ui';
import { BuildingIcon } from "@/shared/ui/components/basic/icons";
import i18next from 'i18next';
import {
  getTypeLabels,
  getPrefLabels,
  getBusinessTypeLabels,
  getPropertyKindLabels,
} from './preference-label-maps';
import { translatePropertyChipLabel } from '@/shared/utils/property-chip-label';

export { getTypeLabels, getPrefLabels, getBusinessTypeLabels, getPropertyKindLabels };

/**
 * Translates a key within the clients feature namespace.
 *
 * @param key - The translation key (relative to the `features/clients` namespace).
 * @returns The translated string.
 */
const t = (key: string): string => i18next.t(key, { ns: 'features/clients' });

/**
 * The ordered set of tab identifiers shown on the client profile panel.
 */
export const TAB_KEYS = ['overview', 'visits', 'propertiesOfInterest', 'documents', 'history', 'proposals'] as const;

/**
 * A single client-profile tab identifier.
 */
export type TabKey = (typeof TAB_KEYS)[number];

/**
 * Resolves the localized labels for each client-profile tab.
 *
 * @returns A map of tab keys to their translated display labels.
 */
export function getTabLabels(): Record<TabKey, string> {
  return {
    overview: t('helpers.tabs.overview'),
    visits: t('helpers.tabs.visits'),
    propertiesOfInterest: t('helpers.tabs.propertiesOfInterest'),
    documents: t('profile.tabs.documents'),
    history: t('profile.tabs.history'),
    proposals: t('profile.tabs.proposals'),
  };
}

interface ContactRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  action?: React.ReactNode;
}

/**
 * Renders a single labeled contact row with an icon and value.
 *
 * @param props - The row content.
 * @param props.icon - The leading icon node.
 * @param props.label - The field label.
 * @param props.value - The field value (falls back to an em dash when empty).
 * @param props.action - Optional trailing action button.
 * @returns The rendered contact row element.
 */
export function ContactRow({ icon, label, value, action }: ContactRowProps) {
  return (
    <div className="flex items-center gap-[var(--space-2)] p-[var(--space-2)] rounded-(--radius-md) bg-[var(--surface-primary)] border border-[var(--border-ultra-subtle)]">
      <Text component="span" className="text-[var(--seed-base)] flex shrink-0 [font-weight:var(--font-bold)]">{icon}</Text>
      <div className="min-w-0 flex-1">
        <div className="text-[length:var(--text-meta)] leading-[var(--leading-snug)] text-[var(--text-tertiary)] truncate">
          {label}
        </div>
        <div className="text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-[var(--text-primary)]">
          {value || "—"}
        </div>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/**
 * Formats a numeric-like preference string as Brazilian currency.
 *
 * @param strVal - The raw price string (may use `.`/`,` separators).
 * @returns The value prefixed with `R$` and locale-formatted when numeric.
 */
function formatPrice(strVal: string): string {
  const cleaned = strVal.replace(/\./g, "").replace(",", ".");
  const num = Number(cleaned);
  if (!isNaN(num) && num > 0) return `R$ ${num.toLocaleString("pt-BR", { minimumFractionDigits: 0 })}`;
  return `R$ ${strVal}`;
}

/**
 * Maps a comma-separated list of property-kind keys to their labels.
 *
 * @param strVal - The comma-separated property-kind keys.
 * @returns A comma-separated string of translated property-kind labels.
 */
function formatPropertyKinds(strVal: string): string {
  const kinds = strVal.split(",");
  const kindLabels = getPropertyKindLabels();
  return kinds.map((k) => kindLabels[k.trim()] || k.trim()).join(", ");
}

/**
 * Normalizes a comma-separated list by trimming each entry.
 *
 * @param strVal - The comma-separated raw list.
 * @returns A cleaned, comma-separated list.
 */
function formatList(strVal: string): string {
  return strVal.split(",").map((s) => s.trim()).join(", ");
}

/**
 * Maps a comma-separated list of amenity tokens to their property labels.
 *
 * The chips are stored as the same tokens the property filter writes
 * (`air_conditioning`, `reception_24h`), so they read as raw storage keys
 * unless they go back through the property vocabulary on the way out.
 *
 * @param strVal - The comma-separated amenity tokens.
 * @returns A comma-separated string of translated amenity labels.
 */
function formatAmenities(strVal: string): string {
  const translate = (key: string, opts?: { defaultValue?: string }) =>
    i18next.t(key, { ns: "features/properties", ...opts }) as string;
  return strVal
    .split(",")
    .map((token) => translatePropertyChipLabel(translate, token.trim()))
    .join(", ");
}

/**
 * Renders the stored pet flag as a sentence a broker can scan.
 *
 * @param strVal - The stringified boolean.
 * @returns The localized yes/no label.
 */
function formatPetFriendly(strVal: string): string {
  const key = strVal === "true" ? "yes" : "no";
  return i18next.t(`form.petOptions.${key}`, { ns: "features/clients" }) as string;
}

const PREF_FORMATTERS: Record<string, (val: string) => string> = {
  business_type: (val) => getBusinessTypeLabels()[val] || val,
  price_min: formatPrice,
  price_max: formatPrice,
  property_kinds: formatPropertyKinds,
  cities: formatList,
  neighborhoods: formatList,
  furnished: formatAmenities,
  pet_friendly: formatPetFriendly,
  amenities_internal: formatAmenities,
  amenities_external: formatAmenities,
  amenities_security: formatAmenities,
  condo_leisure: formatAmenities,
  condo_security: formatAmenities,
  tags: formatList,
};

/**
 * Formats a client preference value for display, applying any field-specific
 * formatter (price, list, or property-kind labels).
 *
 * @param key - The preference key whose value is being formatted.
 * @param val - The raw preference value.
 * @returns The human-readable, formatted preference value.
 */
export function formatPrefValue(key: string, val: unknown): string {
  const strVal = String(val);
  const formatter = PREF_FORMATTERS[canonicalPrefKey(key)];
  if (formatter) return formatter(strVal);
  return strVal;
}

/**
 * Operator suffixes the property-search filter vocabulary appends to a numeric
 * field (`bedrooms_eq`, `bathrooms_lte`). The agent's LLM sometimes emits these
 * for a client's demand preference instead of the canonical `*_min` threshold,
 * so a legacy/unnormalized record can still carry `bedrooms_eq`. Stripping the
 * suffix lets the chip resolve the friendly label and formatter for the
 * canonical field rather than printing the raw storage key.
 */
const PREF_OPERATOR_SUFFIXES = ['_eq', '_lte', '_gte', '_ne'] as const;

/**
 * Numeric demand fields whose canonical stored key is the `*_min` threshold.
 * An operator-suffixed variant (`bedrooms_eq`) or a bare count (`bedrooms`)
 * resolves to the same canonical key so it reads with the friendly label.
 */
const NUMERIC_PREF_BASES: Record<string, string> = {
  bedrooms: 'bedrooms_min',
  bathrooms: 'bathrooms_min',
  suites: 'suites_min',
  parking: 'parking_min',
  area: 'area_min',
};

/**
 * Resolves a stored preference key to the canonical key the label map and
 * formatters are defined against.
 *
 * Handles three shapes the agent/LLM may have persisted for a numeric demand
 * criterion: the canonical `bedrooms_min`, a bare `bedrooms`, and an
 * operator-suffixed filter key such as `bedrooms_eq` / `bedrooms_lte`. All
 * three collapse to `bedrooms_min` so the chip reads "Dormitórios: 2" instead
 * of the raw `bedrooms_eq: 2`. A key that matches nothing is returned verbatim.
 *
 * @param key - The raw stored preference key.
 * @returns The canonical preference key.
 */
export function canonicalPrefKey(key: string): string {
  const base = stripOperatorSuffix(key);
  return NUMERIC_PREF_BASES[base] ?? base;
}

/**
 * Removes a trailing comparison-operator suffix from a preference key.
 *
 * @param key - The raw stored preference key.
 * @returns The key without its `_eq`/`_lte`/`_gte`/`_ne` suffix, when present.
 */
function stripOperatorSuffix(key: string): string {
  for (const suffix of PREF_OPERATOR_SUFFIXES) {
    if (key.endsWith(suffix)) return key.slice(0, -suffix.length);
  }
  return key;
}

/**
 * Resolves the friendly display label for a stored preference key, tolerating
 * operator-suffixed and bare numeric variants.
 *
 * @param labels - The resolved preference label map (from `getPrefLabels`).
 * @param key - The raw stored preference key.
 * @returns The friendly label, or the canonical key when none is mapped.
 */
export function resolvePrefLabel(labels: Record<string, string>, key: string): string {
  const canonical = canonicalPrefKey(key);
  return labels[canonical] ?? labels[key] ?? canonical;
}

/**
 * Normalizes a birth date to `DD/MM/YYYY`, tolerating already-formatted or
 * empty inputs.
 *
 * @param date - The raw birth date (ISO `YYYY-MM-DD`, `DD/MM/YYYY`, or empty).
 * @returns The formatted date, or an em dash when no date is provided.
 */
export function formatBirthDate(date: string | undefined): string {
  if (!date) return "—";
  if (date.includes("/")) return date;
  const parts = date.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return date;
}

const PREF_ICON_STYLE = { color: "var(--text-tertiary)", width: 14, height: 14 };

const PREF_ICONS: Record<string, React.ReactNode> = {
  business_type: <PersonIcon style={PREF_ICON_STYLE} />,
  cities: <DrawingPinFilledIcon style={PREF_ICON_STYLE} />,
  neighborhoods: <DrawingPinFilledIcon style={PREF_ICON_STYLE} />,
  price_min: <IdCardIcon style={PREF_ICON_STYLE} />,
  price_max: <IdCardIcon style={PREF_ICON_STYLE} />,
  property_kinds: <BuildingIcon style={PREF_ICON_STYLE} />,
};

/**
 * Resolves the icon shown next to a client preference field.
 *
 * @param key - The preference key to resolve an icon for.
 * @returns The icon node for the preference (a building icon by default).
 */
export function getPrefIcon(key: string): React.ReactNode {
  return PREF_ICONS[canonicalPrefKey(key)] ?? <BuildingIcon style={PREF_ICON_STYLE} />;
}

export {
  isSyntheticContact,
  extractSocialFromSyntheticEmail,
  getSocialProfileUrl,
  getSocialConversationUrl,
  getSocialProfileLabel,
  getContactLabel,
  getContactHref,
  isSocialContact,
  isConversationLink,
} from './contact-helpers';
